/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { notificheAPI } from '../api/notifiche';

const ACCENT = '#8B5CF6';

export default function NotificheBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data: notifiche = [] } = useQuery({
    queryKey: ['notifiche'],
    queryFn: () => notificheAPI.lista(),
    refetchInterval: 60000,
  });

  const nonLette = notifiche.filter((n) => !n.letta).length;

  const segnaLetta = useMutation({
    mutationFn: (id) => notificheAPI.segnaLetta(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifiche'] }),
  });

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const apri = (n) => {
    if (!n.letta) segnaLetta.mutate(n.id);
    if (n.link) { setOpen(false); navigate(n.link); }
    else if (n.intervento_id) { setOpen(false); navigate(`/interventi/${n.intervento_id}`); }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={styles.bellBtn} aria-label="Notifiche">
        <Bell size={20} />
        {nonLette > 0 && <span style={styles.badge}>{nonLette > 9 ? '9+' : nonLette}</span>}
      </button>

      {open && (
        <div style={styles.dropdown}>
          <div style={styles.header}><span style={{ fontWeight: 700 }}>Notifiche</span></div>
          {notifiche.length === 0 ? (
            <div style={styles.empty}>Nessuna notifica.</div>
          ) : (
            <div style={styles.list}>
              {notifiche.slice(0, 5).map((n) => (
                <div key={n.id} onClick={() => apri(n)} style={{ ...styles.item, backgroundColor: n.letta ? 'white' : '#F3E8FF' }}>
                  <div style={{ ...styles.dot, backgroundColor: n.letta ? 'transparent' : ACCENT }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.itemTitle}>{n.titolo}</div>
                    <div style={styles.itemMsg}>{n.messaggio}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => { setOpen(false); navigate('/notifiche'); }} style={styles.footerBtn}>Vedi tutte</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  bellBtn: { position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', padding: '0.4rem', display: 'flex', alignItems: 'center' },
  badge: { position: 'absolute', top: 0, right: 0, backgroundColor: '#EF4444', color: 'white', fontSize: '0.65rem', fontWeight: 700, minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' },
  dropdown: { position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', width: '320px', maxWidth: '90vw', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', zIndex: 100, overflow: 'hidden' },
  header: { padding: '0.85rem 1rem', borderBottom: '1px solid #F3F4F6' },
  empty: { padding: '2rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.88rem' },
  list: { maxHeight: '320px', overflow: 'auto' },
  item: { display: 'flex', gap: '0.6rem', padding: '0.8rem 1rem', cursor: 'pointer', borderBottom: '1px solid #F9FAFB' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', marginTop: '0.35rem', flexShrink: 0 },
  itemTitle: { fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.15rem' },
  itemMsg: { fontSize: '0.78rem', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  footerBtn: { width: '100%', padding: '0.75rem', backgroundColor: '#F9FAFB', border: 'none', borderTop: '1px solid #F3F4F6', color: ACCENT, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
};
